import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CITIZEN_GET_MOST_POPULAR_SERVICES} from "../portal-home";
import {Apollo} from "apollo-angular";
import {CitizensServices} from "../../../../store/entities/citizens/citizens-services/citizens-services.model";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {SettingsService} from "../../../../services/settings.service";
import {
  PortalPopularServicesViewDetailsComponent
} from "./portal-popular-services-view-details/portal-popular-services-view-details.component";
import {
  citizenGetAllSectors
} from "../../../../store/entities/citizens/citizens-services-sector/citizens-services-sector.actions";
import {Store} from "@ngrx/store";
import {AppState} from "../../../../store/reducers";
import {Observable, Subscription} from "rxjs";
import {
  CitizensServicesSector
} from "../../../../store/entities/citizens/citizens-services-sector/citizens-services-sector.model";
import {
  selectModifiedCitizenSector
} from "../../../../store/entities/citizens/citizens-services-sector/citizens-services-sector.selectors";
import {
  GET_CITIZEN_SERVICES_SECTOR_FEW
} from "../../../../store/entities/citizens/citizens-services/citizens-services.graphql";

@Component({
  selector: 'app-portal-popular-services-view',
  templateUrl: './portal-popular-services-view.component.html',
  styleUrl: './portal-popular-services-view.component.scss'
})
export class PortalPopularServicesViewComponent implements OnInit, AfterViewInit, OnDestroy{

  @ViewChild('tabGrid') tabGrid!: ElementRef<HTMLElement>;

  private readonly apollo: Apollo = inject(Apollo);
  private readonly sanitizer: DomSanitizer = inject(DomSanitizer);
  private readonly store: Store<AppState> = inject(Store<AppState>);
  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  private resizeObserver?: ResizeObserver;
  private adjustTabsFrame?: number;

  private readonly directoryIconColor = '#44548D';
  private readonly sectorIconColor = '#2A4F97';
  private readonly directoryHoverIconColor = '#FFC436';
  private readonly sectorHoverIconColor = '#FFFFFF';

  directoryIcons: Record<string, SafeHtml> = {};
  tabIcons: Record<string, SafeHtml> = {};
  citizenSectors$: Observable<CitizensServicesSector[]>;

  subscription$: Subscription = new Subscription();

  tabs: { name: string; icon: string; slug: string, id?: string}[] = [];

  activeTab = 'popular';
  visibleTabCount = 1;

  get visibleTabs() {
    return this.tabs.slice(0, this.visibleTabCount);
  }


  specificDirectory: {name: string; icon: string; url: string }[] = [
    {
      name: 'Children',
      icon: '/assets/icons/children.svg',
      url: '/service-by-special-group/Children'
    },
    {
      name: 'Youth',
      icon: '/assets/icons/children.svg',
      url: '/service-by-special-group/Youth'
    },
    {
      name: 'Women',
      icon: '/assets/icons/woman.svg',
      url: '/service-by-special-group/Women'
    },
    {
      name: 'Elders',
      icon: '/assets/icons/elderly.svg',
      url: '/service-by-special-group/Elderly'
    },
    {
      name: 'Special Group',
      icon: '/assets/icons/special.svg',
      url: '/service-by-special-group/Special_Needs'
    },
    {
      name: 'Foreigner',
      icon: '/assets/icons/foreigner.svg',
      url: '/service-by-group/G2F'
    }
  ]

  services: CitizensServices[];

  ngOnInit() {

    this.store.dispatch(citizenGetAllSectors({filtering: null}));
    this.citizenSectors$ = this.store.select(selectModifiedCitizenSector);
    this.subscription$.add(this.citizenSectors$.subscribe(data => {
      if (data?.length) {
        this.tabs = [
          {name: 'Popular', icon: 'icon1', slug: 'popular'},
          ...(data.slice(0, 7)?.map(sector => ({id: sector?.sectorUniqueId, name: sector.sectorName, icon: sector.sectorIcon, slug: sector.sectorIcon})) ?? [])
        ]
      } else {
        this.tabs = [
          {name: 'Popular', icon: 'icon1', slug: 'popular'}
        ];
      }
      this.loadTabIcons();
      this.scheduleAdjustVisibleTabs();
    }));
    this.loadTabServices();
    this.loadDirectoryIcons();
  }

  ngAfterViewInit() {
    this.resizeObserver = new ResizeObserver(() => this.scheduleAdjustVisibleTabs());
    if (this.tabGrid?.nativeElement) {
      this.resizeObserver.observe(this.tabGrid.nativeElement);
    }
    this.scheduleAdjustVisibleTabs();
  }

  loadDirectoryIcons() {
    this.specificDirectory.forEach((group) => {
      fetch(group.icon)
        .then((response) => response.text())
        .then((svg) => {
          this.directoryIcons[group.name] = this.sanitizer.bypassSecurityTrustHtml(svg);
        });
    });
  }

  loadTabIcons() {
    this.tabIcons = {};
    const loads = this.tabs.map((tab) => {
      const iconPath = this.getTabIconPath(tab);
      if (!iconPath) {
        return Promise.resolve();
      }

      return fetch(iconPath)
        .then((response) => response.text())
        .then((svg) => {
          this.tabIcons[tab.slug] = this.sanitizer.bypassSecurityTrustHtml(svg);
        });
    });

    Promise.all(loads).then(() => {
      setTimeout(() => {
        this.updateTabIconColors();
        this.scheduleAdjustVisibleTabs();
      });
    });
  }

  private scheduleAdjustVisibleTabs(): void {
    if (this.adjustTabsFrame) {
      cancelAnimationFrame(this.adjustTabsFrame);
    }

    this.adjustTabsFrame = requestAnimationFrame(() => {
      this.adjustVisibleTabs();
      this.adjustTabsFrame = undefined;
    });
  }

  private adjustVisibleTabs(): void {
    if (!this.tabGrid?.nativeElement || !this.tabs.length) {
      return;
    }

    let count = 1;
    while (count <= this.tabs.length) {
      this.visibleTabCount = count;
      this.cdr.detectChanges();

      if (this.hasTabOverflow()) {
        this.visibleTabCount = Math.max(1, count - 1);
        this.cdr.detectChanges();
        break;
      }

      count++;
    }

    const activeIndex = this.tabs.findIndex(tab => tab.slug === this.activeTab);
    if (activeIndex >= this.visibleTabCount) {
      this.changeTab(this.tabs[0].slug);
    } else {
      this.updateTabIconColors();
    }
  }

  private hasTabOverflow(): boolean {
    const tabElements = this.tabGrid.nativeElement.querySelectorAll('[data-tab-slug]');
    return Array.from(tabElements).some((element) => {
      const el = element as HTMLElement;
      return el.scrollWidth > el.clientWidth + 1;
    });
  }

  getTabIconPath(tab: { icon: string; slug: string }): string | null {
    if (tab.slug === 'popular') {
      return `/assets/icons/sector/popular.svg`;
    }
    return `/assets/icons/sector/${tab.icon}.svg`;
  }

  onOpenServiceDetails(service: CitizensServices) {
    this.settingsService.openDialogModal({
      component: PortalPopularServicesViewDetailsComponent,
      width: '90%',
      data: {service}
    });
  }

  onDirectoryIconHover(event: MouseEvent, isHovering: boolean) {
    this.onSvgIconHover(event, isHovering, this.directoryIconColor, this.directoryHoverIconColor);
  }

  onTabIconHover(event: MouseEvent, slug: string, isHovering: boolean) {
    if (slug === this.activeTab) {
      return;
    }

    const container = event.currentTarget as HTMLElement;
    const svg = container.querySelector('svg');
    if (!svg) {
      return;
    }

    if (isHovering) {
      this.applySvgIconColor(svg, this.sectorIconColor, this.sectorHoverIconColor);
    } else {
      this.restoreSvgIconColor(svg);
    }
  }

  updateTabIconColors() {
    const tabElements = this.elementRef.nativeElement.querySelectorAll('[data-tab-slug]');
    tabElements.forEach((element: Element) => {
      const slug = element.getAttribute('data-tab-slug');
      const svg = element.querySelector('svg');
      if (!svg || !slug) {
        return;
      }

      if (slug === this.activeTab) {
        this.applySvgIconColor(svg, this.sectorIconColor, this.sectorHoverIconColor);
      } else {
        this.restoreSvgIconColor(svg);
      }
    });
  }

  applySvgIconColor(svg: SVGElement, defaultColor: string, targetColor: string) {
    const normalizedDefault = defaultColor.toUpperCase();

    svg.querySelectorAll('*').forEach((element) => {
      const el = element as SVGElement;
      const fill = el.getAttribute('fill');
      const stroke = el.getAttribute('stroke');

      if (fill?.toUpperCase() === normalizedDefault) {
        if (!el.hasAttribute('data-original-fill')) {
          el.setAttribute('data-original-fill', fill);
        }
        el.setAttribute('fill', targetColor);
      }
      if (stroke?.toUpperCase() === normalizedDefault) {
        if (!el.hasAttribute('data-original-stroke')) {
          el.setAttribute('data-original-stroke', stroke);
        }
        el.setAttribute('stroke', targetColor);
      }
    });
  }

  restoreSvgIconColor(svg: SVGElement) {
    svg.querySelectorAll('*').forEach((element) => {
      const el = element as SVGElement;

      if (el.hasAttribute('data-original-fill')) {
        el.setAttribute('fill', el.getAttribute('data-original-fill')!);
        el.removeAttribute('data-original-fill');
      }
      if (el.hasAttribute('data-original-stroke')) {
        el.setAttribute('stroke', el.getAttribute('data-original-stroke')!);
        el.removeAttribute('data-original-stroke');
      }
    });
  }

  onSvgIconHover(event: MouseEvent, isHovering: boolean, defaultColor: string, hoverColor: string) {
    const container = event.currentTarget as HTMLElement;
    const svg = container.querySelector('svg');
    if (!svg) {
      return;
    }

    if (isHovering) {
      this.applySvgIconColor(svg, defaultColor, hoverColor);
    } else {
      this.restoreSvgIconColor(svg);
    }
  }

  changeTab(slug: string) {
    this.activeTab = slug;
    this.services = [];
    this.loadTabServices();
    this.updateTabIconColors();
  }

  isLoadingService = false

  loadTabServices() {

    this.isLoadingService = true;
    if (this.activeTab === 'popular') {
      this.getCitizenMostPopularServices();
    } else {
      this.getCitizenServicesBySector(this.tabs.find(tab => tab.slug === this.activeTab)?.id)
    }
  }

  getCitizenMostPopularServices() {
    this.apollo
      .query({
        query: CITIZEN_GET_MOST_POPULAR_SERVICES,
        fetchPolicy: 'network-only',
      })
      .toPromise()
      .then((data) => {
        let result: any = Object.values(data)[0];
        result = result.citizenGetMostPopularServices;
        if (result?.response.code == '9000') {
          this.services = result.data;
          this.services = this.services.slice(0, 12)
        }
      }).finally(() => { this.isLoadingService = false; });
  }

  getCitizenServicesBySector(sectorUniqueId: string) {
    this.apollo
      .query({
        query: GET_CITIZEN_SERVICES_SECTOR_FEW,
        fetchPolicy: 'network-only',
        variables: {
          sectorUniqueId,
          filtering: {
            pageNumber: 1,
            itemsPerPage: 12,
          }
        }
      })
      .toPromise()
      .then((data) => {
        let result: any = Object.values(data)[0];
        result = result.citizenGetServicesFromSector;
        if (result?.response.code == '9000') {
          this.services = result.data;
          this.services = this.services.slice(0, 12)
        }
      }).finally(() => { this.isLoadingService = false; });
  }

  ngOnDestroy() {
    this.subscription$.unsubscribe();
    this.resizeObserver?.disconnect();
    if (this.adjustTabsFrame) {
      cancelAnimationFrame(this.adjustTabsFrame);
    }
  }
}
